import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { PasswordConfirmationModalComponent } from '../password-confirmation-modal/password-confirmation-modal.component';
import { QRCodeModule } from 'angularx-qrcode';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { MFAService } from '@app/pages/login-page/services/verify-totp.service';
import { CODE_LENGTH, CODE_REGEX } from '@app/utils/constants/two-factor.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { TwoFactorStep } from '@app/utils/enums/two-factor.enum';
import { TwoFactorData } from '@app/utils/interfaces/mfa.interface';

@Component({
  selector: 'app-two-factor-setup-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    TranslateModule,
    ReactiveFormsModule,
    QRCodeModule,
    IconComponent,
  ],
  templateUrl: './two-factor-setup-modal.component.html',
  styleUrl: './two-factor-setup-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwoFactorSetupModalComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<PasswordConfirmationModalComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly twoFactorService = inject(MFAService);
  private readonly destroyRef = inject(DestroyRef);

  protected currentStep = signal<TwoFactorStep>(TwoFactorStep.DOWNLOAD);
  protected verificationForm!: FormGroup;
  protected showInvalidCodeError = signal(false);
  protected isLoading = signal(false);
  protected twoFactorData = inject<TwoFactorData>(MAT_DIALOG_DATA);

  public iconName = IconName;

  public ngOnInit(): void {
    this.verificationForm = this.fb.group({
      code: this.fb.array(Array.from({ length: CODE_LENGTH }, () => new FormControl(''))),
    });
  }

  public get codeArray(): FormArray {
    return this.verificationForm.get('code') as FormArray;
  }

  public onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    this.showInvalidCodeError.set(false);

    if (!CODE_REGEX.test(value)) {
      input.value = '';
      return;
    }

    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }
  }

  public onKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && !input.value && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  }

  public onCancel(): void {
    this.dialogRef.close({ completed: false });
  }

  public onBack(): void {
    const current = this.currentStep();

    switch (current) {
      case TwoFactorStep.SCAN_QR:
        this.currentStep.set(TwoFactorStep.DOWNLOAD);
        break;
      case TwoFactorStep.VERIFY_CODE:
        this.currentStep.set(TwoFactorStep.SCAN_QR);
        this.showInvalidCodeError.set(false);
        this.verificationForm.reset();
        break;
      default:
        this.onCancel();
    }
  }

  public async nextStep(): Promise<void> {
    const current = this.currentStep();

    switch (current) {
      case TwoFactorStep.DOWNLOAD:
        this.currentStep.set(TwoFactorStep.SCAN_QR);
        break;
      case TwoFactorStep.SCAN_QR:
        this.currentStep.set(TwoFactorStep.VERIFY_CODE);
        break;
      case TwoFactorStep.VERIFY_CODE:
        await this.handleVerifyCodeStep();
        break;
    }
  }

  protected isDownloadStep(): boolean {
    return this.currentStep() === TwoFactorStep.DOWNLOAD;
  }

  protected isScanQrStep(): boolean {
    return this.currentStep() === TwoFactorStep.SCAN_QR;
  }

  protected isVerifyCodeStep(): boolean {
    return this.currentStep() === TwoFactorStep.VERIFY_CODE;
  }

  protected showBackButton(): boolean {
    return this.currentStep() !== TwoFactorStep.DOWNLOAD;
  }

  protected isNextButtonDisabled(): boolean {
    if (this.isVerifyCodeStep()) {
      const allFieldsFilled = this.codeArray.controls.every(
        (control) => control.value && control.value.trim() !== '',
      );

      return !allFieldsFilled;
    }

    return false;
  }

  protected formatSecretKey(secretKey: string): string {
    if (!secretKey) return '';

    const chunks = [];
    for (let i = 0; i < secretKey.length; i += 4) {
      chunks.push(secretKey.slice(i, i + 4));
    }

    return chunks.join(' ');
  }

  private async handleVerifyCodeStep(): Promise<void> {
    const code = this.codeArray.value.join('');

    if (code.length !== CODE_LENGTH) {
      return;
    }

    this.isLoading.set(true);

    try {
      await firstValueFrom(
        this.twoFactorService.confirm2FA(code).pipe(takeUntilDestroyed(this.destroyRef)),
      );

      this.showInvalidCodeError.set(false);
      this.isLoading.set(false);

      this.dialogRef.close({ completed: true });
    } catch {
      this.isLoading.set(false);
      this.showInvalidCodeError.set(true);
      this.verificationForm.reset();
    }
  }
}
